import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/design02/Header';
import {
  Crown,
  Globe,
  Sparkles,
  Shield,
  Star,
  MapPin,
  Mail,
  Phone,
  Users,
  Truck,
  CheckCircle,
  ArrowRight,
  Award,
  Leaf,
  Heart,
  Zap
} from 'lucide-react';

const HomePage = () => {
  return (
    <>
      {/* Head tags (React 19 hoists these to <head>) */}
      <title>Claude&apos;s Special - Premium Ceylon Cinnamon Exports | Sri Lankan Spices</title>
      <meta
        name="description"
        content="Claude's Special exports the finest Ceylon Cinnamon from Sri Lanka to Australia. Discover authentic, premium quality cinnamon with rich heritage and unmatched flavor."
      />
      <meta
        name="keywords"
        content="Ceylon Cinnamon, Sri Lankan spices, cinnamon export, authentic cinnamon, premium spices, Australia import"
      />
      <meta property="og:title" content="Claude&apos;s Special - Premium Ceylon Cinnamon Exports" />
      <meta
        property="og:description"
        content="Exporting the finest Ceylon Cinnamon from Sri Lanka to Australia with authentic quality and rich heritage."
      />
      <meta property="og:type" content="website" />
      <link rel="canonical" href="/" />
      {/* Optional social image:
      <meta property="og:image" content="https://yourcdn.com/og.jpg" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Claude's Special - Premium Ceylon Cinnamon Exports" />
      <meta name="twitter:description" content="Exporting the finest Ceylon Cinnamon from Sri Lanka to Australia with authentic quality and rich heritage." />
      <meta name="twitter:image" content="https://yourcdn.com/og.jpg" />
      */}

      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 via-teal-800 to-emerald-700"></div>
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://images.pexels.com/photos/4198933/pexels-photo-4198933.jpeg"
              alt="Ceylon Cinnamon plantation"
              className="w-full h-full object-cover object-center"
            />
          </div>
          {/* Enhanced Decorative Elements */}
          <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-amber-400/30 to-orange-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-br from-emerald-400/30 to-teal-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-gradient-to-br from-purple-400/20 to-pink-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>

        <div className="relative z-10 text-center text-white max-w-6xl mx-auto px-4 pt-24 pb-8">
          {/* Enhanced Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md rounded-full px-8 py-4 mb-10 border border-white/20 shadow-2xl hover:bg-white/15 transition-all duration-300">
            <Crown className="h-6 w-6 text-amber-300" />
            <span className="text-base font-semibold">Premium Ceylon Heritage Since 1999</span>
            <Sparkles className="h-5 w-5 text-amber-300 animate-pulse" />
          </div>

          <h1 className="text-7xl md:text-9xl font-black mb-8 leading-tight drop-shadow-2xl">
            <span className="bg-gradient-to-r from-white via-emerald-100 to-white bg-clip-text text-transparent">
              Claude&apos;s
            </span>
            <br />
            <span className="bg-gradient-to-r from-amber-300 via-orange-300 to-amber-300 bg-clip-text text-transparent">
              Special
            </span>
          </h1>

          <p className="text-3xl md:text-4xl mb-8 font-light text-emerald-100 drop-shadow-lg">
            The World&apos;s Finest Ceylon Cinnamon
          </p>

          <p className="text-xl mb-16 opacity-90 max-w-4xl mx-auto leading-relaxed drop-shadow-md">
            From the mystical highlands of Sri Lanka to the discerning markets of Australia,
            we deliver authentic Ceylon Cinnamon that transforms ordinary moments into extraordinary experiences.
          </p>

          <div className="flex flex-col sm:flex-row gap-8 justify-center items-center mb-20">
            <Link
              to="/ceylon-cinnamon"
              className="group bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-12 py-6 rounded-full font-bold text-xl transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-3xl flex items-center space-x-3"
            >
              <span>Discover Our Cinnamon</span>
              <ArrowRight className="h-6 w-6 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <a
              href="#about"
              className="bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 hover:border-white/50 px-12 py-6 rounded-full font-bold text-xl transition-all duration-300 backdrop-blur-md shadow-xl"
            >
              Our Story
            </a>
          </div>

          {/* Enhanced Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-16 pb-16 border-t border-white/20">
            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="text-4xl md:text-5xl font-black text-amber-300 mb-3 drop-shadow-lg">25+</div>
              <div className="text-base text-emerald-200 font-medium">Years Experience</div>
            </div>
            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="text-4xl md:text-5xl font-black text-amber-300 mb-3 drop-shadow-lg">100%</div>
              <div className="text-base text-emerald-200 font-medium">Authentic Ceylon</div>
            </div>
            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="text-4xl md:text-5xl font-black text-amber-300 mb-3 drop-shadow-lg">50+</div>
              <div className="text-base text-emerald-200 font-medium">Export Partners</div>
            </div>
            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="text-4xl md:text-5xl font-black text-amber-300 mb-3 drop-shadow-lg">99.9%</div>
              <div className="text-base text-emerald-200 font-medium">Quality Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white relative overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl opacity-20 transform rotate-12"></div>
              <div className="relative bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 shadow-2xl border border-gray-100">
                <img
                  src="https://images.pexels.com/photos/4198006/pexels-photo-4198006.jpeg"
                  alt="Traditional cinnamon processing"
                  className="w-full h-80 object-cover rounded-2xl mb-6"
                />
                <div className="absolute -bottom-4 -right-4 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-6 shadow-xl text-white">
                  <div className="text-center">
                    <Crown className="h-8 w-8 mx-auto mb-2" />
                    <div className="text-lg font-bold">Premium</div>
                    <div className="text-xs opacity-90">Quality</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 rounded-full px-4 py-2 mb-6">
                <Leaf className="h-4 w-4" />
                <span className="text-sm font-semibold">Our Heritage</span>
              </div>

              <h2 className="text-5xl font-black text-gray-900 mb-6 leading-tight">
                Crafting Excellence
                <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent"> Since 1999</span>
              </h2>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Born from a passion for authentic Sri Lankan heritage, Claude&apos;s Special has become
                the trusted bridge between Ceylon&apos;s ancient cinnamon traditions and Australia&apos;s
                modern culinary landscape.
              </p>

              <div className="space-y-6 mb-10">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Certified Excellence</h3>
                    <p className="text-gray-600">ISO certified processing with HACCP compliance for international standards.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Community Partnership</h3>
                    <p className="text-gray-600">Direct relationships with local farmers ensuring fair trade and sustainability.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                    <Globe className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Global Reach</h3>
                    <p className="text-gray-600">Seamless export operations connecting Sri Lankan quality to Australian markets.</p>
                  </div>
                </div>
              </div>

              <Link
                to="/ceylon-cinnamon"
                className="inline-flex items-center bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-8 py-4 rounded-full font-bold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl space-x-2"
              >
                <span>Learn More</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 rounded-full px-6 py-3 mb-6">
              <Star className="h-5 w-5" />
              <span className="font-semibold">Why Choose Us</span>
            </div>
            <h2 className="text-5xl font-black text-gray-900 mb-6">
              Unmatched Quality &
              <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent"> Service</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every aspect of our operation is designed to deliver the finest Ceylon Cinnamon experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-emerald-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Premium Quality Assurance</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Every batch undergoes rigorous testing and quality control to ensure only the finest Ceylon Cinnamon reaches your customers.
                </p>
                <div className="flex items-center text-emerald-600 font-semibold text-sm">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  <span>ISO Certified Process</span>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-amber-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Zap className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Lightning Fast Export</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Streamlined logistics and customs clearance ensure your orders reach Australia quickly and efficiently.
                </p>
                <div className="flex items-center text-amber-600 font-semibold text-sm">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  <span>7-14 Day Delivery</span>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-purple-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Sustainable Sourcing</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Direct partnerships with local farmers ensure fair trade practices and environmental sustainability.
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  <span>Fair Trade Certified</span>
                </div>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-blue-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Truck className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Flexible Packaging</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Custom packaging solutions from bulk wholesale to retail-ready formats to meet your specific needs.
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  <span>Custom Solutions</span>
                </div>
              </div>
            </div>

            {/* Feature 5 */}
            <div className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-green-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Globe className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Expert Support</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Dedicated export specialists provide personalized support throughout your import journey.
                </p>
                <div className="flex items-center text-green-600 font-semibold text-sm">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>

            {/* Feature 6 */}
            <div className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-rose-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-rose-500/10 to-pink-500/10 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Star className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Competitive Pricing</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Direct sourcing and efficient operations allow us to offer premium Ceylon Cinnamon at competitive wholesale prices.
                </p>
                <div className="flex items-center text-rose-600 font-semibold text-sm">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  <span>Best Value Guarantee</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-emerald-900 via-teal-800 to-emerald-900 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md rounded-full px-6 py-3 mb-8 border border-white/20">
            <Crown className="h-5 w-5 text-amber-300" />
            <span className="text-white font-semibold">Ready to Import?</span>
          </div>

          <h2 className="text-5xl md:text-6xl font-black text-white mb-6 leading-tight">
            Start Your Ceylon
            <span className="bg-gradient-to-r from-amber-300 to-orange-300 bg-clip-text text-transparent"> Cinnamon Journey</span>
          </h2>

          <p className="text-xl text-emerald-100 mb-12 leading-relaxed max-w-3xl mx-auto">
            Join Australia&apos;s leading importers who trust Claude&apos;s Special for authentic Ceylon Cinnamon.
            Get wholesale pricing and expert export support today.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <a
              href="#contact"
              className="group bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-3xl flex items-center space-x-2"
            >
              <span>Get Export Quote</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
            </a>
            <Link
              to="/ceylon-cinnamon"
              className="bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 hover:border-white/50 px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 backdrop-blur-md"
            >
              Learn About Our Cinnamon
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 rounded-full px-6 py-3 mb-6">
              <Mail className="h-5 w-5" />
              <span className="font-semibold">Get In Touch</span>
            </div>
            <h2 className="text-5xl font-black text-gray-900 mb-6">
              Ready to Import
              <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent"> Premium Ceylon Cinnamon?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Contact our export specialists for wholesale pricing and custom solutions
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 border border-emerald-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Export Office</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Location</h4>
                      <p className="text-gray-600">Colombo, Sri Lanka</p>
                      <p className="text-sm text-gray-500">Export Processing Zone</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                      <p className="text-gray-600">+94 11 XXX XXXX</p>
                      <p className="text-sm text-gray-500">Export Hotline</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                      <p className="text-gray-600">exports@claudesspecial.lk</p>
                      <p className="text-sm text-gray-500">24/7 Response</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 border border-amber-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Export Specialties</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm text-gray-700">Cinnamon Sticks</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm text-gray-700">Ground Powder</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm text-gray-700">Essential Oil</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm text-gray-700">Custom Packaging</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 shadow-xl border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Export Inquiry Form</h3>
              <form className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name *</label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-colors duration-200"
                      placeholder="Your company name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Person *</label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-colors duration-200"
                      placeholder="Your name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-colors duration-200"
                    placeholder="your@company.com"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Product Interest</label>
                    <select className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-colors duration-200">
                      <option value="">Select product type</option>
                      <option value="sticks">Cinnamon Sticks</option>
                      <option value="powder">Ground Powder</option>
                      <option value="oil">Essential Oil</option>
                      <option value="mixed">Mixed Products</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity Required</label>
                    <select className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-colors duration-200">
                      <option value="">Select quantity</option>
                      <option value="1-100kg">1-100 kg</option>
                      <option value="100-500kg">100-500 kg</option>
                      <option value="500-1000kg">500-1000 kg</option>
                      <option value="1000kg+">1000+ kg</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                  <textarea
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-colors duration-200 resize-none"
                    placeholder="Tell us about your import requirements, preferred packaging, delivery timeline, etc."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                >
                  <span>Send Export Inquiry</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center">
                  <Crown className="h-7 w-7 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold">Claude&apos;s Special</div>
                  <div className="text-sm text-emerald-400">Ceylon Cinnamon Exports</div>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed mb-6 max-w-md">
                Bringing the authentic taste and health benefits of Ceylon Cinnamon from Sri Lanka&apos;s highlands
                to Australia&apos;s discerning markets with uncompromising quality and service.
              </p>
              <div className="flex space-x-4">
                <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center hover:bg-emerald-700 transition-colors duration-200 cursor-pointer">
                  <span className="text-sm font-bold">🇱🇰</span>
                </div>
                <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center hover:bg-emerald-700 transition-colors duration-200 cursor-pointer">
                  <span className="text-sm font-bold">🇦🇺</span>
                </div>
              </div>
            </div>

            {/* Products */}
            <div>
              <h4 className="text-lg font-bold mb-6 text-emerald-400">Our Products</h4>
              <ul className="space-y-3 text-gray-400">
                <li className="hover:text-white transition-colors duration-200 cursor-pointer">Ceylon Cinnamon Sticks</li>
                <li className="hover:text-white transition-colors duration-200 cursor-pointer">Ground Cinnamon Powder</li>
                <li className="hover:text-white transition-colors duration-200 cursor-pointer">Cinnamon Essential Oil</li>
                <li className="hover:text-white transition-colors duration-200 cursor-pointer">Custom Packaging</li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold mb-6 text-emerald-400">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/ceylon-cinnamon" className="text-gray-400 hover:text-white transition-colors duration-200">
                    About Ceylon Cinnamon
                  </Link>
                </li>
                <li>
                  <a href="#contact" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Export Inquiry
                  </a>
                </li>
                <li>
                  <a href="#about" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Our Story
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              &copy; 2025 Claude&apos;s Special. All rights reserved. | Export License: SL-EXP-2025-001
            </p>
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <span>🌿 100% Authentic Ceylon</span>
              <span>🏆 ISO Certified</span>
              <span>🚚 Fast Export</span>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default HomePage;
